import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, 
  TrendingDown,
  DollarSign,
  BarChart3,
  Filter
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import dayjs from 'dayjs';

// API et Types
import { ExchangeRatesService } from '../../services/exchangeRates';
import { 
  ExchangeRateFilters,
  ExchangeRateData,
  CurrencyPair
} from '../../types/exchangeRates';

// UI Components
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Button } from '../../components/ui/button';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { Label } from '../../components/ui/label';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { CalendarIcon } from 'lucide-react';
import { cn } from '../../utils/utils';

// Chart components
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

export const ExchangeRatesPage: React.FC = () => {
  const { currentUser } = useAuth();
  
  // États
  const [exchangeRateData, setExchangeRateData] = useState<ExchangeRateData[]>([]);
  const [currencyPairs, setCurrencyPairs] = useState<CurrencyPair[]>([]);
  const [filters, setFilters] = useState<ExchangeRateFilters>({
    startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
    currencyPair: 'EUR/USD'
  });
  const [selectedPreset, setSelectedPreset] = useState<string>('last7days');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Récupérer les données de taux de change
  const fetchExchangeRateData = async (currentFilters?: ExchangeRateFilters) => {
    try {
      setIsLoading(true);
      setError(null);
      const finalFilters = currentFilters || filters;
  const response = await ExchangeRatesService.getExchangeRates(finalFilters);
  setExchangeRateData(response.data || []);
    } catch (error: any) {
      console.error('Error fetching exchange rate data:', error);
      setError('Erreur lors de la récupération des taux de change');
    } finally {
      setIsLoading(false);
    }
  };

  // Récupérer les paires de devises disponibles
  const fetchCurrencyPairs = async () => {
    try {
  const response = await ExchangeRatesService.getAvailableCurrencyPairs();
  setCurrencyPairs(response.pairs || []);
    } catch (error: any) {
      console.error('Error fetching currency pairs:', error);
    }
  };

  // Charger les données initiales
  useEffect(() => {
    fetchCurrencyPairs();
    fetchExchangeRateData();
  }, []);

  // Fonction pour détecter quel preset correspond aux dates actuelles
  const detectPresetFromDates = (startDate?: string, endDate?: string): string => {
    if (!startDate || !endDate) return '';
    
    const now = dayjs();
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    
    const isEndToday = end.isSame(now, 'day');
    
    if (isEndToday) {
      const daysDiff = now.diff(start, 'day');
      if (daysDiff === 7) return 'last7days';
      if (daysDiff === 14) return 'last14days';
      if (daysDiff === 30) return 'last30days';
    }
    
    const lastMonth = now.subtract(1, 'month');
    const isLastMonth = start.isSame(lastMonth.startOf('month'), 'day') && 
                       end.isSame(lastMonth.endOf('month'), 'day');
    if (isLastMonth) return 'lastMonth';
    
    return '';
  };

  // Synchroniser selectedPreset avec les dates
  useEffect(() => {
    const detectedPreset = detectPresetFromDates(filters.startDate, filters.endDate);
    setSelectedPreset(detectedPreset);
  }, [filters.startDate, filters.endDate]);

  // Gestionnaire de changement de preset de dates
  const handlePresetChange = (preset: string) => {
    const now = dayjs();
    let startDate: string;
    let endDate = now.format('YYYY-MM-DD');

    switch (preset) {
      case 'last7days':
        startDate = now.subtract(7, 'day').format('YYYY-MM-DD');
        break;
      case 'last14days':
        startDate = now.subtract(14, 'day').format('YYYY-MM-DD');
        break;
      case 'last30days':
        startDate = now.subtract(30, 'day').format('YYYY-MM-DD');
        break;
      case 'lastMonth':
        const lastMonth = now.subtract(1, 'month');
        startDate = lastMonth.startOf('month').format('YYYY-MM-DD');
        endDate = lastMonth.endOf('month').format('YYYY-MM-DD');
        break;
      default:
        return;
    }

    setSelectedPreset(preset);
    const newFilters = { ...filters, startDate, endDate };
    setFilters(newFilters);
    fetchExchangeRateData(newFilters);
  };

  // Gestionnaires de changement de filtres
  const handleStartDateChange = (date: Date | undefined) => {
    if (date) {
      setSelectedPreset('');
      const newFilters = { ...filters, startDate: dayjs(date).format('YYYY-MM-DD') };
      setFilters(newFilters);
      fetchExchangeRateData(newFilters);
    }
  };

  const handleEndDateChange = (date: Date | undefined) => {
    if (date) {
      setSelectedPreset('');
      const newFilters = { ...filters, endDate: dayjs(date).format('YYYY-MM-DD') };
      setFilters(newFilters);
      fetchExchangeRateData(newFilters);
    }
  };

  const handleCurrencyPairChange = (currencyPair: string) => {
    const newFilters = { ...filters, currencyPair };
    setFilters(newFilters);
    fetchExchangeRateData(newFilters);
  };

  const resetFilters = () => {
    setSelectedPreset('last7days');
    const newFilters = {
      startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
      currencyPair: 'EUR/USD'
    };
    setFilters(newFilters);
    fetchExchangeRateData(newFilters);
  };

  // Calculs pour le summary (avec vérifications défensives)
  const safeExchangeRateData = exchangeRateData || [];
  const currentRate = safeExchangeRateData.length > 0 ? safeExchangeRateData[safeExchangeRateData.length - 1].rate : 0;
  const previousRate = safeExchangeRateData.length > 1 ? safeExchangeRateData[safeExchangeRateData.length - 2].rate : currentRate;
  const variation = currentRate > 0 && previousRate > 0 ? ((currentRate - previousRate) / previousRate) * 100 : 0;
  const averageRate = safeExchangeRateData.length > 0 ? 
    safeExchangeRateData.reduce((sum, item) => sum + item.rate, 0) / safeExchangeRateData.length : 0;
  const rates = safeExchangeRateData.map(item => item.rate);
  const minRate = rates.length > 0 ? Math.min(...rates) : 0;
  const maxRate = rates.length > 0 ? Math.max(...rates) : 0;

  // Formater les valeurs
  const formatRate = (value: number) => {
    return value.toFixed(6);
  };

  const formatPercentage = (value: number) => {
    return `${value >= 0 ? '+' : ''}${value.toFixed(2)}%`;
  };

  // Configuration du graphique
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      x: {
        ticks: {
          maxTicksLimit: 3
        }
      },
      y: {
        beginAtZero: false
      }
    }
  };

  // Fonction pour créer les cartes de graphiques avec métriques
  const renderChart = (title: string, children: React.ReactNode, metric?: { value: number; type: 'currency' | 'percentage' | 'number' }) => (
    <Card>
      <CardHeader>
        <CardTitle className="text-sm font-bold flex justify-between items-center">
          <span>{title}</span>
          {metric && (
            <span className="text-sm font-bold text-foreground">
              {metric.type === 'currency' ? formatRate(metric.value) : 
               metric.type === 'percentage' ? formatPercentage(metric.value) :
               metric.value.toFixed(6)}
            </span>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-80">
          {children}
        </div>
      </CardContent>
    </Card>
  );

  // Données du graphique (avec vérifications défensives)
  const chartData = {
    labels: safeExchangeRateData.map(item => 
      dayjs(item.date).format('DD/MM')
    ),
    datasets: [
      {
        label: filters.currencyPair,
        data: safeExchangeRateData.map(item => item.rate),
        borderColor: '#3b82f6',
        backgroundColor: '#3b82f620',
        fill: true,
        tension: 0.4,
      },
    ],
  };

  if (!currentUser) {
    return (
      <div className="p-6">
        <Alert variant="destructive">
          <AlertTitle>Erreur</AlertTitle>
          <AlertDescription>Vous devez être connecté pour accéder à cette page.</AlertDescription>
        </Alert>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-7rem)] overflow-auto">
      <div className="p-6 space-y-6">
        {/* En-tête */}
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-3xl font-bold">Taux de Change</h1>
            <p className="text-muted-foreground mt-1">
              Suivi des taux de change en temps réel
            </p>
          </div>
        </div>

        {/* Filtres */}
        <div className="animate-in fade-in duration-500 delay-300">
          {isLoading ? (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Filtres
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="h-10 bg-muted rounded animate-pulse"></div>
                  <div className="h-10 bg-muted rounded animate-pulse"></div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardHeader className="pb-4">
                <CardTitle className="flex items-center gap-2">
                  <Filter className="h-5 w-5" />
                  Filtres
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-6">
                  {/* Presets de dates avec tabs horizontales */}
                  <div className="space-y-2">
                    <Label>Période</Label>
                    <Tabs value={selectedPreset} onValueChange={handlePresetChange} className="w-full">
                      <TabsList className="grid w-full grid-cols-4 h-auto">
                        <TabsTrigger value="last7days" className="py-2 px-3 w-auto">
                          7 jours
                        </TabsTrigger>
                        <TabsTrigger value="last14days" className="py-2 px-3 w-auto">
                          14 jours
                        </TabsTrigger>
                        <TabsTrigger value="last30days" className="py-2 px-3 w-auto">
                          30 jours
                        </TabsTrigger>
                        <TabsTrigger value="lastMonth" className="py-2 px-3 w-auto">
                          Mois dernier
                        </TabsTrigger>
                      </TabsList>
                    </Tabs>
                  </div>

                  {/* Filtres à droite */}
                  <div className="flex-1">
                    <div className="grid grid-cols-4 gap-3">
                      {/* Date de début */}
                      <div className="space-y-1 pt-2">
                        <Label className="text-sm">Date début</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full h-9 justify-start text-left font-normal",
                                !filters.startDate && "text-muted-foreground"
                              )}
                              disabled={isLoading}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {filters.startDate ? (
                                dayjs(filters.startDate).format('DD/MM/YYYY')
                              ) : (
                                <span>Sélectionner</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={filters.startDate ? dayjs(filters.startDate).toDate() : undefined}
                              onSelect={handleStartDateChange}
                              disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Date de fin */}
                      <div className="space-y-1 pt-2">
                        <Label className="text-sm">Date fin</Label>
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button
                              variant="outline"
                              className={cn(
                                "w-full h-9 justify-start text-left font-normal",
                                !filters.endDate && "text-muted-foreground"
                              )}
                              disabled={isLoading}
                            >
                              <CalendarIcon className="mr-2 h-4 w-4" />
                              {filters.endDate ? (
                                dayjs(filters.endDate).format('DD/MM/YYYY')
                              ) : (
                                <span>Sélectionner</span>
                              )}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar
                              mode="single"
                              selected={filters.endDate ? dayjs(filters.endDate).toDate() : undefined}
                              onSelect={handleEndDateChange}
                              disabled={(date) => {
                                return date > new Date() || 
                                       date < new Date("1900-01-01") ||
                                       (filters.startDate ? date < dayjs(filters.startDate).toDate() : false);
                              }}
                              initialFocus
                            />
                          </PopoverContent>
                        </Popover>
                      </div>

                      {/* Paire de devises */}
                      <div className="space-y-1 pt-2">
                        <Label className="text-sm">Paire de devises</Label>
                        <Select value={filters.currencyPair} onValueChange={handleCurrencyPairChange}>
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder="Sélectionner une paire" />
                          </SelectTrigger>
                          <SelectContent>
                            {currencyPairs && currencyPairs.length > 0 ? (
                              currencyPairs.map((pair) => (
                                <SelectItem key={pair.pair} value={pair.pair}>
                                  {pair.pair}
                                </SelectItem>
                              ))
                            ) : (
                              // Paires par défaut si les données ne sont pas chargées
                              <>
                                <SelectItem value="EUR/USD">EUR/USD</SelectItem>
                                <SelectItem value="USD/EUR">USD/EUR</SelectItem>
                                <SelectItem value="GBP/USD">GBP/USD</SelectItem>
                                <SelectItem value="USD/JPY">USD/JPY</SelectItem>
                              </>
                            )}
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Bouton de réinitialisation */}
                      <div className="space-y-1 pt-2">
                        <Label className="text-sm">&nbsp;</Label>
                        <Button 
                          variant="outline" 
                          onClick={resetFilters}
                          disabled={isLoading}
                          className="w-full h-9"
                        >
                          Réinitialiser
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Messages d'erreur */}
        {error && (
          <Alert variant="destructive">
            <AlertTitle>Erreur</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Métriques de résumé */}
        {safeExchangeRateData.length > 0 && (
          <Card className="animate-in fade-in duration-500 delay-500">
            <CardHeader>
              <CardTitle>Résumé - {filters.currencyPair}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card className="border-none shadow-sm bg-gradient-to-r from-blue-500 to-blue-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          Taux Actuel
                        </h3>
                        <DollarSign className="h-6 w-6 opacity-90" />
                      </div>
                      <div className="text-2xl font-bold">
                        {formatRate(currentRate)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm bg-gradient-to-r from-green-500 to-green-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          Variation
                        </h3>
                        {variation >= 0 ? 
                          <TrendingUp className="h-6 w-6 opacity-90" /> : 
                          <TrendingDown className="h-6 w-6 opacity-90" />
                        }
                      </div>
                      <div className="text-2xl font-bold">
                        {formatPercentage(variation)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm bg-gradient-to-r from-orange-500 to-orange-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          Moyenne
                        </h3>
                        <BarChart3 className="h-6 w-6 opacity-90" />
                      </div>
                      <div className="text-2xl font-bold">
                        {formatRate(averageRate)}
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-none shadow-sm bg-gradient-to-r from-purple-500 to-purple-600">
                    <CardContent className="p-6 text-white">
                      <div className="flex items-center justify-between space-y-0 pb-2">
                        <h3 className="tracking-tight text-sm font-medium opacity-90">
                          Fourchette
                        </h3>
                        <TrendingUp className="h-6 w-6 opacity-90" />
                      </div>
                      <div className="text-2xl font-bold">
                        {formatRate(minRate)} - {formatRate(maxRate)}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Graphique */}
        {safeExchangeRateData.length > 0 && (
          <div className="animate-in fade-in duration-500 delay-700">
            {renderChart(
              `Évolution du taux ${filters.currencyPair}`,
              <Line data={chartData} options={chartOptions} />,
              { 
                value: currentRate, 
                type: 'currency' 
              }
            )}
          </div>
        )}

        {/* État de chargement */}
        {isLoading && safeExchangeRateData.length === 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array(4).fill(0).map((_, i) => (
              <Card key={i}>
                <CardHeader className="space-y-0 pb-2">
                  <div className="h-4 bg-muted rounded animate-pulse"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-8 bg-muted rounded animate-pulse"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};